// A message the agent wrote is WAITING (the send rule): the chat chip, the task's "Waiting to send" rows and the
// stage card's Cancel all read and act on it through this module. Web's side of
// `native-apps/shared/send-hold/contract.json`, the fixture iOS `SendHold.swift` and Android `SendHold.kt` run too.
//
// Deliberately dependency-free (see orion-web CLAUDE.md, "a unit test must not import the app's dependency graph"):
// the parse MIRRORS common-lib's `readSendHoldCard` (`utils/SendHoldCard.ts`) rather than importing it. Keep the two
// in step — the shared fixture is what holds them together.

export type SendHoldBucket = 'ATTENDED_APPROVED' | 'BACKGROUND' | 'MEETING';
/** `sendNow` is offered only on a MEETING hold (meeting hold, Q4): the reader drops it from any other bucket. */
export type SendHoldAction = 'cancel' | 'sendNow';

export interface SendHoldHold {
  /** The held send's id (a GUID): what CancelMyHeldSend takes. */
  holdId: string;
  /** When it goes out, ISO-8601 UTC, exactly as the harness wrote it. */
  fireAt: string;
  bucket: SendHoldBucket;
  /** Who it goes to, as the owner calls them. */
  to: string;
  /** Lower-case channel name (`whatsapp`, `sms`, `email`, ...). */
  channel: string;
  actions: SendHoldAction[];
}

export interface SendHoldCard {
  holds: SendHoldHold[];
}

export const SEND_HOLD_CARD_MAX_HOLDS = 10;
export const SEND_HOLD_ASK_PREFIX = 'send-hold:';
/** How long a hold's row stays after its send time ("Sending…"), before it leaves the screen. */
export const SEND_HOLD_LINGER_MS = 30_000;
const HOUR_MS = 60 * 60 * 1000;

const BUCKETS: readonly SendHoldBucket[] = ['ATTENDED_APPROVED', 'BACKGROUND', 'MEETING'];
const ACTIONS: readonly SendHoldAction[] = ['cancel', 'sendNow'];
/** The meeting summary row's card: the same holds under their own key (contract `meetingHoldsCard`). */
export const MEETING_HOLDS_CARD_KEY = 'meetingHoldsCard';
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** UTC only: a `Z` suffix. An offset is refused rather than converted, so every surface reads the same instant. */
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

export function isSendHoldGuid(value: unknown): value is string {
  return typeof value === 'string' && GUID.test(value);
}

function readHold(raw: unknown): SendHoldHold | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const { holdId, fireAt, bucket, to, channel, actions } = raw as Record<string, unknown>;
  if (!isSendHoldGuid(holdId)) return undefined;
  if (typeof fireAt !== 'string' || !ISO_UTC.test(fireAt) || Number.isNaN(Date.parse(fireAt))) return undefined;
  if (typeof bucket !== 'string' || !BUCKETS.includes(bucket as SendHoldBucket)) return undefined;
  if (typeof to !== 'string' || !to.trim()) return undefined;
  if (typeof channel !== 'string' || !channel.trim()) return undefined;
  const known = Array.isArray(actions)
    ? actions.filter((a): a is SendHoldAction => ACTIONS.includes(a as SendHoldAction) && (a !== 'sendNow' || bucket === 'MEETING'))
    : [];
  return { holdId, fireAt, bucket: bucket as SendHoldBucket, to, channel, actions: [...new Set(known)] };
}

/** Read `metadata.sendHoldCard`, fail closed PER HOLD; undefined when no hold survives. A JSON string is not decoded. */
export function readSendHoldCard(metadata: unknown): SendHoldCard | undefined {
  return readHoldsCard((metadata as { sendHoldCard?: unknown } | null | undefined)?.sendHoldCard);
}

/**
 * Read `metadata.meetingHoldsCard` — the meeting summary's card, with the same hold rules; never read from the chat
 * chip's key (nor the other way round). Its liveness is read BY ITS HOLD IDS, never by the summary row's conversation.
 */
export function readMeetingHoldsCard(metadata: unknown): SendHoldCard | undefined {
  return readHoldsCard((metadata as Record<string, unknown> | null | undefined)?.[MEETING_HOLDS_CARD_KEY]);
}

function readHoldsCard(raw: unknown): SendHoldCard | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const list = (raw as { holds?: unknown }).holds;
  if (!Array.isArray(list)) return undefined;
  const holds: SendHoldHold[] = [];
  for (const item of list) {
    const hold = readHold(item);
    if (hold && !holds.some((h) => h.holdId === hold.holdId)) holds.push(hold);
    if (holds.length === SEND_HOLD_CARD_MAX_HOLDS) break;
  }
  return holds.length > 0 ? { holds } : undefined;
}

// ── What a hold reads as ─────────────────────────────────────────────────────────────────────────

export type SendHoldDisplay = { kind: 'sendsAt'; text: string } | { kind: 'countdown'; text: string } | { kind: 'sending' };

const timeFormats = new Map<string, Intl.DateTimeFormat>();
function hourMinute(zone: string): Intl.DateTimeFormat {
  let format = timeFormats.get(zone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone });
    timeFormats.set(zone, format);
  }
  return format;
}

/** HH:MM (24h) in `timeZone`, falling back to UTC when the zone is not one Intl knows. */
export function formatHoldTime(at: Date, timeZone: string | undefined): string {
  try {
    return hourMinute(timeZone || 'UTC').format(at);
  } catch {
    return hourMinute('UTC').format(at);
  }
}

/** Over an hour away: the send time. An hour or less: m:ss, rounded UP. At or past the send time: sending. */
export function sendHoldDisplay(fireAt: Date, now: Date, timeZone: string | undefined): SendHoldDisplay {
  const left = fireAt.getTime() - now.getTime();
  if (left <= 0) return { kind: 'sending' };
  if (left > HOUR_MS) return { kind: 'sendsAt', text: formatHoldTime(fireAt, timeZone) };
  const seconds = Math.ceil(left / 1000);
  return { kind: 'countdown', text: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` };
}

/** The i18n key and params a display renders through. */
export function sendHoldDisplayLine(display: SendHoldDisplay): { key: string; params?: Record<string, string> } {
  switch (display.kind) {
    case 'sendsAt':
      return { key: 'outreach.line.sendsAt', params: { time: display.text } };
    case 'countdown':
      return { key: 'outreach.line.sendsIn', params: { time: display.text } };
    case 'sending':
      return { key: 'contacts.viewer.sending' };
  }
}

// ── Cancel ───────────────────────────────────────────────────────────────────────────────────────

export type SendHoldCancelResponse = 'CANCELED' | 'TOO_LATE' | 'NOT_FOUND' | 'ERROR';
export type SendHoldOutcome = 'canceled' | 'tooLate' | 'cancelFailed' | 'ended';
export type SendHoldCancelOutcome = { outcome: SendHoldOutcome; keepButton: boolean };

/** How a Cancel answer reads. Fails toward cancelling: a NOT_FOUND inside the window keeps the button. */
export function sendHoldCancelOutcome(response: SendHoldCancelResponse | string, fireAt: Date, now: Date): SendHoldCancelOutcome {
  switch (response) {
    case 'CANCELED':
      return { outcome: 'canceled', keepButton: false };
    case 'TOO_LATE':
      return { outcome: 'tooLate', keepButton: false };
    case 'NOT_FOUND':
      return now.getTime() < fireAt.getTime() ? { outcome: 'cancelFailed', keepButton: true } : { outcome: 'ended', keepButton: false };
    default:
      return { outcome: 'cancelFailed', keepButton: true };
  }
}

/** symphony-bff's prefixed outcome (`CANCEL_MY_HELD_SEND_OUTCOME_CANCELED`) → the contract's name; anything else → ERROR. */
export function cancelResponseFromWire(outcome: unknown): SendHoldCancelResponse {
  if (typeof outcome !== 'string') return 'ERROR';
  const name = outcome.startsWith('CANCEL_MY_HELD_SEND_OUTCOME_') ? outcome.slice('CANCEL_MY_HELD_SEND_OUTCOME_'.length) : outcome;
  return name === 'CANCELED' || name === 'TOO_LATE' || name === 'NOT_FOUND' ? name : 'ERROR';
}

/** symphony-bff's prefixed bucket (`HELD_SEND_BUCKET_BACKGROUND`) → the contract's name; anything else → undefined. */
export function bucketFromWire(bucket: unknown): SendHoldBucket | undefined {
  if (typeof bucket !== 'string') return undefined;
  const name = bucket.startsWith('HELD_SEND_BUCKET_') ? bucket.slice('HELD_SEND_BUCKET_'.length) : bucket;
  return BUCKETS.includes(name as SendHoldBucket) ? (name as SendHoldBucket) : undefined;
}

/** The BI `bucket` field: the contract name lower-cased, `unknown` when the surface has none. */
export function biBucket(bucket: SendHoldBucket | undefined): string {
  return bucket ? bucket.toLowerCase() : 'unknown';
}

export const SEND_HOLD_CANCEL_RETRY_DELAY_MS = 1_000;

/**
 * Run a Cancel, retrying ONCE (after ~1s) when it could not get an answer. Safe: CancelHeldSend is idempotent.
 * `call` resolves to the contract response; it must never throw, but a throw is read as ERROR all the same.
 */
export async function cancelHeldSendWithRetry(
  call: () => Promise<SendHoldCancelResponse>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<SendHoldCancelResponse> {
  const attempt = async () => {
    try {
      return await call();
    } catch {
      return 'ERROR' as const;
    }
  };
  const first = await attempt();
  if (first !== 'ERROR') return first;
  await wait(SEND_HOLD_CANCEL_RETRY_DELAY_MS);
  return attempt();
}

// ── Send now (meeting hold) ──────────────────────────────────────────────────────────────────────

/** Where a Send now answer lands — contract `sendNowOutcome`. */
export type SendHoldSendNowPhase = 'sendNowSending' | 'sentNow' | 'sendNowCanceled' | 'ended' | 'sendNowFailed';

/** A SendMyHeldSendNow answer as `/api/held-sends/<id>/send-now` returns it (bare or prefixed names). */
export interface SendNowResponse {
  outcome?: unknown;
  state?: unknown;
  sendAt?: unknown;
}

/**
 * How a Send now answer reads. Fails toward NOT sending: "Sent" only for SENT; a failed call (`undefined`), NOT_FOUND
 * or an unknown outcome keeps both buttons (`sendNowFailed`); NOT_ALLOWED reads by the hold's state.
 */
export function sendHoldSendNowPhase(response: SendNowResponse | null | undefined): SendHoldSendNowPhase {
  const outcome = typeof response?.outcome === 'string' ? response.outcome.replace(/^SEND_MY_HELD_SEND_NOW_OUTCOME_/, '') : undefined;
  switch (outcome) {
    case 'SCHEDULED':
    case 'ALREADY_SENDING':
      return 'sendNowSending';
    case 'SENT':
      return 'sentNow';
    case 'CANCELED':
      return 'sendNowCanceled';
    case 'NOT_SENT':
      return 'ended';
    case 'NOT_ALLOWED':
      switch (heldSendStateFromWire(response?.state)) {
        case 'SENDING':
        case 'MAYBE_SENT':
          return 'sendNowSending';
        case 'SENT':
          return 'sentNow';
        case 'CANCELED':
          return 'sendNowCanceled';
        case 'DROPPED':
        case 'FAILED':
          return 'ended';
        default:
          return 'sendNowFailed';
      }
    default:
      return 'sendNowFailed';
  }
}

/** The `{time}` of "Couldn't send now. It will go at {time}.": the answer's send time, else the hold's own. */
export function sendNowFailedTime(fireAt: Date, responseSendAt: string | undefined, timeZone: string | undefined): string {
  const answered = responseSendAt ? new Date(responseSendAt) : undefined;
  return formatHoldTime(answered && !Number.isNaN(answered.getTime()) ? answered : fireAt, timeZone);
}

// ── The live read (ListMyHeldSends) ──────────────────────────────────────────────────────────────

export interface HeldSendRecipient {
  displayName?: string;
  channel?: string;
  outreachId?: string;
}

/** One waiting message as `/api/held-sends` answers it. */
export interface HeldSendRow {
  heldSendId: string;
  sendAt: string;
  bucket?: SendHoldBucket;
  recipients: HeldSendRecipient[];
  textPreview?: string;
  taskId?: string;
  taskTitle?: string;
  conversationId?: string;
  canCancel: boolean;
  /** The bff offers Send now on it (HELD_SEND_ACTION_SEND_NOW): a HELD MEETING hold not already sent now. */
  canSendNow: boolean;
  /** Send now was already asked for: still HELD, its time is now — "Sending…", Cancel only. */
  sendNowRequested: boolean;
  /** The hold's state, bare (`HELD`, `SENT`, …). A list read says HELD; a by-id read also returns finished holds. */
  state?: HeldSendState;
}

export type HeldSendState = 'HELD' | 'SENDING' | 'SENT' | 'CANCELED' | 'DROPPED' | 'FAILED' | 'MAYBE_SENT';
const HELD_SEND_STATES: readonly HeldSendState[] = ['HELD', 'SENDING', 'SENT', 'CANCELED', 'DROPPED', 'FAILED', 'MAYBE_SENT'];

/** symphony-bff's prefixed state (`HELD_SEND_STATE_SENT`) → the bare name; anything else → undefined. */
export function heldSendStateFromWire(state: unknown): HeldSendState | undefined {
  if (typeof state !== 'string') return undefined;
  const name = state.startsWith('HELD_SEND_STATE_') ? state.slice('HELD_SEND_STATE_'.length) : state;
  return HELD_SEND_STATES.includes(name as HeldSendState) ? (name as HeldSendState) : undefined;
}

export interface HeldSendsList {
  rows: HeldSendRow[];
  /** Some rows (or their details) could not be read: a hold missing here is NOT known to have ended. */
  partial: boolean;
  /** The page was full: a hold missing here is NOT known to have ended. */
  truncated: boolean;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * The wire answer → rows. A row with no GUID id or no readable send time is dropped, and then the list is marked
 * partial: a row we could not read must never read as "not held" (that would take a live Cancel away).
 */
export function parseHeldSendsList(raw: unknown): HeldSendsList | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const body = raw as { heldSends?: unknown; rows?: unknown; partial?: unknown; truncated?: unknown };
  const items = Array.isArray(body.heldSends) ? body.heldSends : Array.isArray(body.rows) ? body.rows : [];
  let partial = body.partial === true;
  const rows: HeldSendRow[] = [];
  for (const item of items) {
    const r = (item ?? {}) as Record<string, unknown>;
    const sendAt = r.sendAt instanceof Date ? r.sendAt.toISOString() : str(r.sendAt);
    if (!isSendHoldGuid(r.heldSendId) || !sendAt || Number.isNaN(Date.parse(sendAt))) {
      partial = true;
      continue;
    }
    const actions = Array.isArray(r.availableActions) ? r.availableActions : Array.isArray(r.actions) ? r.actions : [];
    const recipients = (Array.isArray(r.recipients) ? r.recipients : []).map((p) => {
      const o = (p ?? {}) as Record<string, unknown>;
      return {
        ...(str(o.displayName) ? { displayName: str(o.displayName) } : {}),
        ...(str(o.channel) ? { channel: str(o.channel) } : {}),
        ...(str(o.outreachId) ? { outreachId: str(o.outreachId) } : {}),
      };
    });
    const bucket = bucketFromWire(r.bucket);
    const state = heldSendStateFromWire(r.state);
    rows.push({
      heldSendId: r.heldSendId,
      sendAt,
      ...(bucket ? { bucket } : {}),
      recipients,
      ...(str(r.textPreview) ? { textPreview: str(r.textPreview) } : {}),
      ...(str(r.taskId) ? { taskId: str(r.taskId) } : {}),
      ...(str(r.taskTitle) ? { taskTitle: str(r.taskTitle) } : {}),
      ...(str(r.conversationId) ? { conversationId: str(r.conversationId) } : {}),
      canCancel: actions.some((a) => a === 'HELD_SEND_ACTION_CANCEL' || a === 'CANCEL' || a === 'cancel'),
      canSendNow: actions.some((a) => a === 'HELD_SEND_ACTION_SEND_NOW' || a === 'SEND_NOW' || a === 'sendNow'),
      sendNowRequested: r.sendNowRequested === true,
      ...(state ? { state } : {}),
    });
  }
  return { rows, partial, truncated: body.truncated === true };
}

export type SendHoldLiveState = 'held' | 'ended' | 'unknown';

/** Whether a hold is still waiting, per the live read. A failed, partial or truncated read never ends one. */
export function sendHoldLiveState(holdId: string, list: HeldSendsList | null | undefined): SendHoldLiveState {
  if (!list) return 'unknown';
  const row = list.rows.find((r) => r.heldSendId === holdId);
  // A by-id read returns finished holds too: listed in any state but HELD is no longer waiting.
  if (row) return !row.state || row.state === 'HELD' ? 'held' : 'ended';
  return list.partial || list.truncated ? 'unknown' : 'ended';
}

/** Whether a live row (the task's "Waiting to send", a by-id read) still offers Send now — contract `liveness.rowCases`. */
export function heldSendRowOffersSendNow(row: HeldSendRow): boolean {
  return row.bucket === 'MEETING' && (!row.state || row.state === 'HELD') && row.canSendNow && !row.sendNowRequested;
}

export type SendHoldLiveView = { live: SendHoldLiveState; fireAt: string; offersSendNow: boolean };

/**
 * A card hold seen through the live read that covers it (`list` undefined/null = no covering read) — contract
 * `liveness`: its live state, its send time (the live row's whenever one is read, S2) and whether Send now is offered.
 */
export function sendHoldLiveView(hold: SendHoldHold, list: HeldSendsList | null | undefined): SendHoldLiveView {
  const live = sendHoldLiveState(hold.holdId, list);
  const row = list?.rows.find((r) => r.heldSendId === hold.holdId);
  const cardOffers = hold.bucket === 'MEETING' && hold.actions.includes('sendNow');
  // A covering read that does not list the hold (even a partial one) never offers Send now; no read: the card decides.
  const offersSendNow = !cardOffers ? false : row ? heldSendRowOffersSendNow(row) : !list;
  return { live, fireAt: row?.sendAt ?? hold.fireAt, offersSendNow };
}

/**
 * The live state, but only from a read that COVERS the hold: a read that started before this screen first saw the
 * hold (a cached list from an earlier turn, or a read in flight when the reply landed) knows nothing about it, so the
 * hold reads unknown — never ended — until a newer read. Mirrors iOS `SendHoldRegistry` and Android
 * `SendHoldController`.
 */
export function sendHoldCoveredLiveState(
  holdId: string,
  list: HeldSendsList | null | undefined,
  readStartedAt: number | undefined,
  holdSeenAt: number,
): SendHoldLiveState {
  if (!list || readStartedAt === undefined || readStartedAt < holdSeenAt) return 'unknown';
  return sendHoldLiveState(holdId, list);
}

/**
 * The task section's rows across reads: every row of the newest successful read, plus every row an earlier read
 * showed that this one does not — kept on screen (its live state says whether it ended) until it hides on its own
 * clock. Soonest first. Mirrors iOS `TaskHeldSends` / Android `mergeTaskHeldSends`.
 */
export function mergeTaskHeldSends(previous: readonly HeldSendRow[], next: HeldSendsList | null | undefined): HeldSendRow[] {
  if (!next) return [...previous];
  const ids = new Set(next.rows.map((r) => r.heldSendId));
  const kept = previous.filter((r) => !ids.has(r.heldSendId));
  return [...next.rows, ...kept].sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt));
}

/** The `{name}` of the "To" line, bidi-isolated (FSI…PDI): the line follows the UI's direction, the name its own. */
export function isolateName(name: string): string {
  return `\u2068${name}\u2069`;
}

// ── One hold's state on screen ───────────────────────────────────────────────────────────────────

/**
 * One hold row's phase, shared by Cancel and Send now (contract `phases`): `sendingNow` = the Send now call is out,
 * `sendNowSending` = accepted ("Sending…"), and the rest are answers.
 */
/**
 * The `{channel}` word of `sendHold.to` → its label key, by the word with everything but a-z/0-9 dropped (contract
 * `channelLabel.keys`, the same table on iOS and Android). The word is a CommunicationChannel name (whatsapp, sms,
 * email, app), a connector's toolkit slug (gmail, outlook, slack, microsoftteams) or whatsapp_group.
 */
export const SEND_HOLD_CHANNEL_KEYS: Readonly<Record<string, string>> = {
  whatsapp: 'contacts.channel.whatsapp',
  sms: 'contacts.channel.sms',
  email: 'contacts.channel.email',
  app: 'contacts.channel.app',
  gmail: 'contacts.channel.gmail_connector',
  gmailconnector: 'contacts.channel.gmail_connector',
  whatsappgroup: 'contacts.channel.whatsapp_group',
  outlook: 'contacts.channel.outlook',
  zohomail: 'contacts.channel.zohomail',
  instagram: 'contacts.channel.instagram',
  facebook: 'contacts.channel.facebook',
  twitter: 'contacts.channel.twitter',
  apollo: 'contacts.channel.apollo',
  brevo: 'contacts.channel.brevo',
  basecamp: 'contacts.channel.basecamp',
  clickup: 'contacts.channel.clickup',
  jira: 'contacts.channel.jira',
  mailchimp: 'contacts.channel.mailchimp',
  microsoftteams: 'contacts.channel.microsoftteams',
  paypal: 'contacts.channel.paypal',
  salesforce: 'contacts.channel.salesforce',
  sendgrid: 'contacts.channel.sendgrid',
  shopify: 'contacts.channel.shopify',
  slack: 'contacts.channel.slack',
  slackbot: 'contacts.channel.slack',
};

/** An unknown word as a label: split on spaces, `_` and `-`, each word's first letter upper-cased. */
function titleCaseChannel(word: string): string {
  return word
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * A hold's channel as a word (contract `channelLabel`): the table's key, translated; blank reads as `app`; an unknown
 * word, or a key with no translation, is the raw word title-cased. Never empty.
 */
export function sendHoldChannelLabel(channel: string | null | undefined, t: (key: string) => string | undefined): string {
  const word = (channel ?? '').trim() || 'app';
  const key = SEND_HOLD_CHANNEL_KEYS[word.toLowerCase().replace(/[^a-z0-9]/g, '')];
  const translated = key ? t(key)?.trim() : undefined;
  return translated || titleCaseChannel(word) || word;
}

export type SendHoldPhase = 'idle' | 'cancelling' | 'sendingNow' | SendHoldOutcome | SendHoldSendNowPhase;

export type SendHoldEvent =
  | { type: 'tap' }
  | { type: 'outcome'; outcome: SendHoldCancelOutcome }
  | { type: 'sendNowTap' }
  | { type: 'sendNowOutcome'; phase: SendHoldSendNowPhase }
  | { type: 'live'; state: SendHoldLiveState };

/**
 * The shared reducer: a local answer always wins over the live read, a read never takes a Cancel away, and only one
 * call per hold is out at a time (each tap is ignored while the other is in flight).
 */
export function sendHoldReduce(phase: SendHoldPhase, event: SendHoldEvent): SendHoldPhase {
  switch (event.type) {
    case 'tap':
      return phase === 'idle' || phase === 'cancelFailed' || phase === 'sendNowFailed' || phase === 'sendNowSending' ? 'cancelling' : phase;
    case 'outcome':
      return phase === 'cancelling' ? (event.outcome.keepButton ? 'cancelFailed' : event.outcome.outcome) : phase;
    case 'sendNowTap':
      return phase === 'idle' || phase === 'cancelFailed' || phase === 'sendNowFailed' ? 'sendingNow' : phase;
    case 'sendNowOutcome':
      return phase === 'sendingNow' ? event.phase : phase;
    case 'live':
      // A live `ended` is a per-hold verdict of the latest covering read, so a later read that lists the hold HELD lifts
      // it again. (A server's own `ended` answer is recorded page-wide and wins over this local phase anyway.)
      if (event.state === 'held' && phase === 'ended') return 'idle';
      return event.state === 'ended' && (phase === 'idle' || phase === 'cancelFailed' || phase === 'sendNowFailed') ? 'ended' : phase;
  }
}

export interface SendHoldUi {
  showsCancel: boolean;
  cancelEnabled: boolean;
  /** ANDed with the hold's own `offersSendNow` by the row. */
  showsSendNow: boolean;
  sendNowEnabled: boolean;
  showsTime: boolean;
  /** The status line's i18n key, when the phase has one. */
  line: string | null;
}

const ui = (showsCancel: boolean, cancelEnabled: boolean, showsSendNow: boolean, sendNowEnabled: boolean, showsTime: boolean, line: string | null): SendHoldUi => ({
  showsCancel,
  cancelEnabled,
  showsSendNow,
  sendNowEnabled,
  showsTime,
  line,
});

/** What a phase shows — contract `phases.ui`, the one table every row reads. */
export function sendHoldUi(phase: SendHoldPhase): SendHoldUi {
  switch (phase) {
    case 'idle':
      return ui(true, true, true, true, true, null);
    case 'cancelling':
      return ui(true, false, true, false, true, null);
    case 'cancelFailed':
      return ui(true, true, true, true, true, 'sendHold.cancelFailed');
    case 'sendingNow':
      return ui(true, false, true, false, true, null);
    case 'sendNowSending':
      return ui(true, true, false, false, false, 'sendHold.sending');
    case 'sendNowFailed':
      return ui(true, true, true, true, false, 'sendHold.sendNowFailed');
    case 'canceled':
      return ui(false, false, false, false, false, 'sendHold.canceled');
    case 'tooLate':
      return ui(false, false, false, false, false, 'sendHold.tooLate');
    case 'ended':
      return ui(false, false, false, false, false, 'sendHold.ended');
    case 'sentNow':
      return ui(false, false, false, false, false, 'sendHold.sentNow');
    case 'sendNowCanceled':
      return ui(false, false, false, false, false, 'sendHold.sendNowCanceled');
  }
}

/** Whether a phase still offers Cancel. */
export function sendHoldShowsCancel(phase: SendHoldPhase): boolean {
  return sendHoldUi(phase).showsCancel;
}

/** Whether a phase still shows the send time / countdown. */
export function sendHoldShowsTime(phase: SendHoldPhase): boolean {
  return sendHoldUi(phase).showsTime;
}

/** The outcome line, when the phase has one. */
export function sendHoldPhaseKey(phase: SendHoldPhase): string | undefined {
  return sendHoldUi(phase).line ?? undefined;
}

/** A server's FINAL answer, recorded page-wide per hold (first wins). A failure or an in-flight phase never is. */
export function sendHoldIsFinal(phase: SendHoldPhase): boolean {
  return phase === 'canceled' || phase === 'tooLate' || phase === 'ended' || phase === 'sentNow' || phase === 'sendNowCanceled';
}

/**
 * A hold leaves the screen 30s after its send time — except the owner's own answers ("Cancelled", "Sent", "It was
 * cancelled"), which stay. A sent-now row's send time is the answer's (`sendHoldSentNowFireAt`), so it leaves 30s later.
 */
export function sendHoldVisible(fireAt: Date, now: Date, phase: SendHoldPhase): boolean {
  return phase === 'canceled' || phase === 'sentNow' || phase === 'sendNowCanceled' || now.getTime() < fireAt.getTime() + SEND_HOLD_LINGER_MS;
}

/** On `sendNowSending` the row's send time becomes the answer's sendAt when that is earlier than fireAt, else the moment of the answer (contract `phases.visibility`). */
export function sendHoldSentNowFireAt(fireAt: Date, responseSendAt: string | undefined, answeredAt: Date): Date {
  const answered = responseSendAt ? new Date(responseSendAt) : undefined;
  return answered && !Number.isNaN(answered.getTime()) && answered.getTime() < fireAt.getTime() ? answered : answeredAt;
}

/** When the on-screen clock must next wake, or undefined when nothing changes any more. */
export function sendHoldNextWake(fireAt: Date, now: Date, phase: SendHoldPhase): number | undefined {
  if (phase === 'sendNowSending') {
    const hideIn = fireAt.getTime() + SEND_HOLD_LINGER_MS - now.getTime();
    return hideIn > 0 ? hideIn : undefined;
  }
  if (!sendHoldShowsTime(phase)) return undefined;
  const left = fireAt.getTime() - now.getTime();
  if (left > HOUR_MS) return left - HOUR_MS;
  // Wake on the next whole-second boundary of the countdown (it shows ceil(left/1000)), so the text never lags a
  // tick behind the clock.
  if (left > 0) return left % 1000 === 0 ? 1000 : left % 1000;
  const hideIn = left + SEND_HOLD_LINGER_MS;
  return hideIn > 0 ? hideIn : undefined;
}

// ── The superseded rule's carve-out ─────────────────────────────────────────────────────────────

export function isSendHoldAskId(askId: string | undefined | null): boolean {
  return typeof askId === 'string' && askId.startsWith(SEND_HOLD_ASK_PREFIX);
}

/** Where a Cancel was tapped — the bff's `surface` and the BI page. */
export type SendHoldSurface = 'chat' | 'task' | 'thread' | 'meeting';

export const SEND_HOLD_BI_PAGE: Record<SendHoldSurface, string> = {
  chat: 'chat',
  task: 'task_details',
  thread: 'contact_thread',
  // The meeting summary's card sits in the chat.
  meeting: 'chat',
};
